"use client";

import { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { ArrowUp, Mic, MousePointer2, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import demoStyles from "@/components/pages/ai-features/product-demos.module.scss";
import styles from "./coach.module.scss";
import { ASK_EXAMPLES, ASK_UI } from "./coachData";

/**
 * Figma "Tela · Ask" (3165:31048): the Ask TS Coach screen inside the orange
 * stage. The option pills are real buttons; selecting one types the question
 * into the input and "sends" it, and the parent mirrors it in the sticky chat.
 * While autoplay is on (card active and in view) it cycles the options itself.
 */

const TYPING_CPS = 32;
const AUTOPLAY_EVERY = 7600;
const EASE = [0.16, 1, 0.3, 1] as const;

type Phase = "idle" | "typing" | "sent";

export default function AskStage({
  selected,
  revision,
  autoplay,
  onSelect,
}: {
  selected: number;
  /** Increments on every selection so the same option can replay. */
  revision: number;
  autoplay: boolean;
  onSelect: (index: number) => void;
}) {
  const root = useRef<HTMLDivElement>(null);
  const windowRef = useRef<HTMLDivElement>(null);
  const sendRef = useRef<HTMLSpanElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const inView = useInView(root, { amount: 0.4 });
  const reduced = useReducedMotion();
  const [phase, setPhase] = useState<Phase>("idle");
  const [typed, setTyped] = useState(0);
  const [cursor, setCursor] = useState<{ x: number; y: number; down: boolean } | null>(null);

  // Park the cursor in the empty area of the screen so the first move travels.
  useEffect(() => {
    const win = windowRef.current;
    if (!win || reduced) return;
    setCursor({ x: win.offsetWidth * 0.42, y: win.offsetHeight * 0.3, down: false });
  }, [reduced]);
  const question = ASK_EXAMPLES[selected]?.question ?? "";

  // Autoplay: cycle through the options while the card is the active one.
  useEffect(() => {
    if (!autoplay || !inView || reduced) return;
    const id = window.setInterval(() => {
      if (document.hidden) return;
      onSelect((selected + 1) % ASK_EXAMPLES.length);
    }, AUTOPLAY_EVERY);
    return () => window.clearInterval(id);
  }, [autoplay, inView, reduced, selected, onSelect]);

  // Selection sequence: cursor to the pill, click, type, cursor to send, send.
  useEffect(() => {
    if (revision === 0) return;
    if (reduced) {
      setPhase("idle");
      setTyped(0);
      return;
    }
    const win = windowRef.current;
    const pill = optionRefs.current[selected];
    const send = sendRef.current;
    const point = (el: Element | null, dx = 0.5, dy = 0.5) => {
      if (!el || !win) return null;
      const a = el.getBoundingClientRect();
      const b = win.getBoundingClientRect();
      const scale = win.offsetWidth / (b.width || 1);
      return {
        x: (a.left - b.left + a.width * dx) * scale,
        y: (a.top - b.top + a.height * dy) * scale,
      };
    };
    const timers: number[] = [];
    const at = (ms: number, fn: () => void) => timers.push(window.setTimeout(fn, ms));
    const pillPoint = point(pill, 0.62, 0.5);
    const sendPoint = point(send);
    const chars = Array.from(question).length;
    const typingMs = (chars / TYPING_CPS) * 1000;

    setPhase("idle");
    setTyped(0);
    if (pillPoint) setCursor({ ...pillPoint, down: false });
    at(520, () => pillPoint && setCursor({ ...pillPoint, down: true }));
    at(680, () => pillPoint && setCursor({ ...pillPoint, down: false }));
    at(760, () => setPhase("typing"));
    let frame = 0;
    const start = performance.now() + 760;
    const tick = (now: number) => {
      const n = Math.min(chars, Math.floor(((now - start) / 1000) * TYPING_CPS));
      if (n >= 0) setTyped(n);
      if (n < chars) frame = requestAnimationFrame(tick);
    };
    at(760, () => {
      frame = requestAnimationFrame(tick);
    });
    at(760 + typingMs + 150, () => sendPoint && setCursor({ ...sendPoint, down: false }));
    at(760 + typingMs + 650, () => sendPoint && setCursor({ ...sendPoint, down: true }));
    at(760 + typingMs + 800, () => {
      if (sendPoint) setCursor({ ...sendPoint, down: false });
      setPhase("sent");
    });
    at(760 + typingMs + 1500, () => {
      setPhase("idle");
      setTyped(0);
    });
    at(760 + typingMs + 2200, () => {
      if (pillPoint) setCursor({ x: pillPoint.x - 140, y: pillPoint.y + 90, down: false });
    });
    return () => {
      timers.forEach((t) => window.clearTimeout(t));
      cancelAnimationFrame(frame);
    };
  }, [selected, revision, reduced, question]);

  const typing = phase === "typing";
  const shown = typing ? Array.from(question).slice(0, typed).join("") : "";

  return (
    <div
      ref={root}
      className={cn(demoStyles.workflowScene, demoStyles.morningScene)}
      data-demo-scene="ask"
    >
      <div
        ref={windowRef}
        className={cn(demoStyles.productWindow, styles.askWindow)}
      >
        <div className={styles.askFooter}>
          <div className={styles.askReplies}>
            <span>{ASK_UI.repliesLabel}</span>
            <fieldset className={styles.askOptions}>
              <legend className="sr-only">{ASK_UI.repliesLabel}</legend>
              {ASK_EXAMPLES.map((example, i) => {
                const active = i === selected;
                return (
                  <button
                    key={example.question}
                    ref={(el) => {
                      optionRefs.current[i] = el;
                    }}
                    type="button"
                    tabIndex={-1}
                    className={styles.askOption}
                    data-selected={active}
                    aria-pressed={active}
                    onClick={() => onSelect(i)}
                  >
                    {active && (
                      <motion.i
                        layoutId="ask-option-fill"
                        className={styles.askOptionFill}
                        transition={{ duration: 0.5, ease: [...EASE] }}
                      />
                    )}
                    <span>{example.question}</span>
                  </button>
                );
              })}
            </fieldset>
          </div>
          <div className={styles.askInputGroup}>
            <div className={styles.askInput} aria-hidden="true">
              <div>
                <Plus />
                <span className={styles.askPlaceholder} data-typing={typing}>
                  {typing ? shown : ASK_UI.placeholder}
                  {typing && <i className={styles.askCaret} />}
                </span>
              </div>
              <div>
                <Mic />
                <motion.span
                  ref={sendRef}
                  className={styles.askSend}
                  animate={
                    phase === "sent"
                      ? { scale: [1, 0.82, 1.08, 1] }
                      : { scale: 1 }
                  }
                  transition={{ duration: 0.5, ease: "easeOut" }}
                >
                  <ArrowUp />
                </motion.span>
              </div>
            </div>
            <p className={styles.askDisclaimer}>{ASK_UI.disclaimer}</p>
          </div>
        </div>
        {cursor && (
          <motion.span
            className={styles.askCursor}
            aria-hidden="true"
            initial={false}
            animate={{
              x: cursor.x,
              y: cursor.y,
              scale: cursor.down ? 0.82 : 1,
              opacity: 1,
            }}
            transition={{
              x: { duration: 0.55, ease: [...EASE] },
              y: { duration: 0.55, ease: [...EASE] },
              scale: { duration: 0.12 },
            }}
          >
            <MousePointer2 fill="white" stroke="#111" strokeWidth={1.1} />
          </motion.span>
        )}
      </div>
    </div>
  );
}
